import React, { useState, useEffect } from 'react';
import { essApi } from '../../services/hrmApi';
import LoadingSpinner from '../Common/LoadingSpinner';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    UserIcon,
    BriefcaseIcon,
    BanknotesIcon,
    IdentificationIcon,
    PhoneIcon,
    ShieldCheckIcon,
    DocumentTextIcon,
    MapPinIcon,
    AcademicCapIcon,
    ClockIcon,
    BuildingOfficeIcon,
    CheckBadgeIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';

// Shared View Field Component
const ViewField = ({ label, value, subValue, className = "", fullWidth = false }) => (
    <div className={`space-y-1 ${className} ${fullWidth ? 'col-span-full' : ''}`}>
        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-tight">
            {label}
        </label>
        <div className="relative">
            <div className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-gray-50/50 sm:text-xs sm:leading-6 min-h-[36px] flex flex-col justify-center">
                <span className="truncate font-semibold text-gray-800">{value || <span className="text-gray-300 font-normal italic">Not Provided</span>}</span>
                {subValue && <span className="text-[10px] text-gray-500 mt-0.5">{subValue}</span>}
            </div>
        </div>
    </div>
);

const SectionHeader = ({ title, icon: Icon, color = "indigo" }) => (
    <div className="flex items-center gap-2 pb-2 mb-4 border-b border-gray-100 mt-6 first:mt-0">
        {Icon && <Icon className={`w-4 h-4 text-${color}-500`} />}
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-widest">{title}</h3>
    </div>
);

const ViewProfileSelf = () => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('identity');
    const [notLinked, setNotLinked] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await essApi.getViewProfileSelf();
                if (response.data.success) {
                    setProfile(response.data.data);
                } else {
                    toast.error(response.data.message || 'Failed to fetch profile details');
                }
            } catch (error) {
                console.error('Error fetching profile:', error);
                if (error.response?.status === 404 && (error.response?.data?.message?.includes('not linked') || error.response?.data?.message?.includes('no employee'))) {
                    setNotLinked(true);
                } else {
                    toast.error('Error loading profile details');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, []);

    if (loading) return <div className="flex justify-center py-20"><LoadingSpinner size="md" /></div>;

    if (notLinked) {
        return (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center bg-white rounded-2xl border border-gray-200 shadow-sm min-h-[450px] max-w-xl mx-auto my-8">
                <div className="p-4 bg-amber-50 text-amber-600 rounded-full mb-6 ring-8 ring-amber-50/50">
                    <UserIcon className="w-12 h-12" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">No Employee Account Found</h3>
                <p className="text-sm text-gray-600 max-w-md mb-6 leading-relaxed">
                    Currently, there is no employee account linked to this user profile.
                </p>
                <div className="flex gap-4">
                    <button 
                        onClick={() => window.location.reload()} 
                        className="px-5 py-2.5 text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all shadow-sm"
                    >
                        Retry
                    </button>
                    <a 
                        href="mailto:hr@company.com" 
                        className="px-5 py-2.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md transition-all hover:shadow-indigo-100"
                    >
                        Contact HR
                    </a>
                </div>
            </div>
        );
    }

    if (!profile) return <div className="text-center py-10 text-gray-500">No profile data found.</div>;

    const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

    const tabs = [
        { id: 'identity', label: 'Master Details', icon: UserIcon },
        { id: 'contact', label: 'Contact & Addresses', icon: MapPinIcon },
        { id: 'organizational', label: 'Organizational', icon: BuildingOfficeIcon },
        { id: 'employment', label: 'Lifecycle & Service', icon: BriefcaseIcon },
        { id: 'financial', label: 'Banking & Payroll', icon: BanknotesIcon },
        { id: 'others', label: 'Emergency & Docs', icon: ShieldCheckIcon }
    ];

    return (
        <div className="space-y-6 max-w-full">
            {/* Primary Tab Navigation Pills */}
            <div className="flex space-x-1 bg-white p-1 rounded-xl shadow-sm border border-gray-200 overflow-x-auto scrollbar-hide">
                {tabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest rounded-lg transition-all whitespace-nowrap ${isActive
                                ? 'bg-indigo-600 text-white shadow-md'
                                : 'text-gray-500 hover:text-indigo-600 hover:bg-indigo-50'
                                }`}
                        >
                            <tab.icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Main Content Pane */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden min-h-[600px]">
                <div className="p-6 md:p-8">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, x: 5 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -5 }}
                            transition={{ duration: 0.15 }}
                        >
                            {/* TAB: Master Identity */}
                            {activeTab === 'identity' && (
                                <div className="space-y-10">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-4">
                                            <SectionHeader title="Basic Personal Information" icon={UserIcon} />
                                        </div>
                                        <ViewField label="Salutation" value={profile.title} />
                                        <ViewField label="Name Initial" value={profile.nameInitial} />
                                        <ViewField label="Surname" value={profile.surname} />
                                        <ViewField label="Display Name" value={profile.displayName} />

                                        <ViewField label="First Name" value={profile.firstName} />
                                        <ViewField label="Middle Name" value={profile.middleName} />
                                        <ViewField label="Last Name" value={profile.lastName} />
                                        <ViewField label="Employee No" value={profile.employeeId} />

                                        <ViewField label="Name As Per Initials" value={profile.nameAsPerInitials} className="md:col-span-2" />
                                        <ViewField label="System Full Name" value={profile.fullName} className="md:col-span-2" />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-4">
                                            <SectionHeader title="Legal & Identity Records" icon={IdentificationIcon} />
                                        </div>
                                        <ViewField label="National Identity (NIC)" value={profile.nic} />
                                        <ViewField label="Date of Birth" value={formatDate(profile.dateOfBirth)} />
                                        <ViewField label="Gender" value={profile.gender} />
                                        <ViewField label="Current Age" value={profile.age ? `${profile.age} Years` : null} />

                                        <ViewField label="Nationality" value={profile.nationality} />
                                        <ViewField label="Religion" value={profile.religion} />
                                        <ViewField label="Marital Status" value={profile.maritalStatus} />
                                        <ViewField label="Blood Group" value={profile.bloodGroup} />

                                        <ViewField label="Passport Number" value={profile.passport?.passportNo} />
                                        <ViewField label="Passport Issue" value={formatDate(profile.passport?.issueDate)} />
                                        <ViewField label="Passport Expiry" value={formatDate(profile.passport?.expiryDate)} />
                                        <ViewField label="Issuing Country" value={profile.passport?.issuingCountry} />

                                        <ViewField label="Driving License" value={profile.drivingLicense?.licenseNo} />
                                        <ViewField label="License Issue" value={formatDate(profile.drivingLicense?.issueDate)} />
                                        <ViewField label="License Expiry" value={formatDate(profile.drivingLicense?.expiryDate)} />
                                    </div>
                                </div>
                            )}

                            {/* TAB: Contact & Address */}
                            {activeTab === 'contact' && (
                                <div className="space-y-10">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-4">
                                            <SectionHeader title="Communication Channels" icon={PhoneIcon} />
                                        </div>
                                        <ViewField label="Work Email (Official)" value={profile.email} className="md:col-span-2" />
                                        <ViewField label="Personal Email" value={profile.personalEmail} className="md:col-span-2" />
                                        <ViewField label="Mobile Connectivity" value={profile.mobileNo} />
                                        <ViewField label="Residential Landline" value={profile.landlineNo} />
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2 mb-2 p-2 bg-indigo-50 rounded-lg">
                                                <MapPinIcon className="w-4 h-4 text-indigo-600" />
                                                <h4 className="text-[10px] font-black text-indigo-900 uppercase tracking-widest leading-none">Current Residence</h4>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50/50 p-4 rounded-2xl border border-gray-100 shadow-inner">
                                                <ViewField label="Address Line 1" value={profile.currentAddress?.addressLine1} className="col-span-full" />
                                                <ViewField label="Address Line 2" value={profile.currentAddress?.addressLine2} className="col-span-full" />
                                                <ViewField label="City" value={profile.currentAddress?.city} />
                                                <ViewField label="State / District" value={profile.currentAddress?.state} />
                                                <ViewField label="Postal Code" value={profile.currentAddress?.postalCode} />
                                                <ViewField label="Country" value={profile.currentAddress?.country} />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div className="flex items-center gap-2 mb-2 p-2 bg-slate-50 rounded-lg">
                                                <CheckBadgeIcon className="w-4 h-4 text-slate-600" />
                                                <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest leading-none">Permanent Address</h4>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50/50 p-4 rounded-2xl border border-gray-100 shadow-inner">
                                                {profile.sameAsCurrent ? (
                                                    <div className="col-span-full flex flex-col items-center justify-center py-10 opacity-30">
                                                        <InformationCircleIcon className="w-8 h-8 mb-2" />
                                                        <p className="text-[10px] font-black uppercase tracking-tighter">Matches Current Residence</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <ViewField label="Address Line 1" value={profile.permanentAddress?.addressLine1} className="col-span-full" />
                                                        <ViewField label="Address Line 2" value={profile.permanentAddress?.addressLine2} className="col-span-full" />
                                                        <ViewField label="City" value={profile.permanentAddress?.city} />
                                                        <ViewField label="State / District" value={profile.permanentAddress?.state} />
                                                        <ViewField label="Postal Code" value={profile.permanentAddress?.postalCode} />
                                                        <ViewField label="Country" value={profile.permanentAddress?.country} />
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TAB: Organizational Assignment */}
                            {activeTab === 'organizational' && (
                                <div className="space-y-10">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-4">
                                            <SectionHeader title="Corporate Placement" icon={BuildingOfficeIcon} color="blue" />
                                        </div>
                                        <ViewField label="Primary Location" value={profile.locationId?.locationName || profile.locationId?.name} />
                                        <ViewField label="Main Department" value={profile.departmentId?.departmentName} />
                                        <ViewField label="Organizational Division" value={profile.divisionId?.divisionName} />
                                        <ViewField label="Designation / Title" value={profile.positionId?.designationName || profile.positionId?.positionName} />

                                        <ViewField label="Pay Grade / Scale" value={profile.gradeId?.gradeName} />
                                        <ViewField label="Cost Center" value={profile.costCenterId?.costCenterName || profile.costCenterId} />
                                        <ViewField label="Work Schedule" value={profile.workSchedule} />
                                        <ViewField label="Unit / Team" value="Main Execution" />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                        <div className="space-y-4">
                                            <SectionHeader title="Management Hierarchy" icon={UserIcon} color="blue" />
                                            <div className="bg-blue-50/30 p-5 rounded-2xl border border-blue-100 space-y-4 shadow-sm">
                                                <ViewField label="Direct Reporting Manager" value={profile.reportingManagerId ? `${profile.reportingManagerId.firstName} ${profile.reportingManagerId.lastName}` : null} subValue={profile.reportingManagerId?.employeeId} />
                                                <ViewField label="Functional Manager" value={profile.functionalManagerId ? `${profile.functionalManagerId.firstName} ${profile.functionalManagerId.lastName}` : null} subValue={profile.functionalManagerId?.employeeId} />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <SectionHeader title="Approval Workflow Hierarchy" icon={CheckBadgeIcon} color="blue" />
                                            <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 border-dashed space-y-2.5">
                                                {profile.supervisorIds?.length > 0 ? profile.supervisorIds.map((sup, idx) => (
                                                    <div key={idx} className="flex justify-between items-center bg-white p-2.5 rounded-lg border border-slate-200 shadow-sm">
                                                        <span className="text-[11px] font-bold text-gray-700">{sup.firstName} {sup.lastName}</span>
                                                        <span className="text-[9px] font-black bg-slate-100 text-slate-500 px-2 py-0.5 rounded tracking-widest uppercase">Level {profile.supervisorIds.length - idx}</span>
                                                    </div>
                                                )) : (
                                                    <p className="text-[10px] text-gray-400 italic text-center py-4">No specific supervisors assigned</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TAB: Service Lifecycle */}
                            {activeTab === 'employment' && (
                                <div className="space-y-10">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-4">
                                            <SectionHeader title="Employment Status Logs" icon={ClockIcon} color="emerald" />
                                        </div>
                                        <ViewField label="Employment Type" value={profile.employmentType} />
                                        <ViewField label="Contract Status" value={profile.employmentStatus} />
                                        <ViewField label="Current System Status" value={profile.status} />
                                        <ViewField label="Onboarding State" value={profile.onboardingStatus} />

                                        <ViewField label="Date of Joining" value={formatDate(profile.joinDate)} />
                                        <ViewField label="Service Period" value={profile.yearsOfService ? `${profile.yearsOfService} Years` : null} />
                                        <ViewField label="Confirmation Target" value={formatDate(profile.confirmationDate)} />
                                        <ViewField label="Retirement Projection" value={formatDate(profile.retirementDate)} />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-4">
                                            <SectionHeader title="Milestone History" icon={BriefcaseIcon} color="emerald" />
                                        </div>
                                        <ViewField label="Last Promotion Date" value={formatDate(profile.promotionDate)} />
                                        <ViewField label="Last Transfer Date" value={formatDate(profile.transferDate)} />
                                        <ViewField label="Notice Period" value={profile.noticePeriodDays ? `${profile.noticePeriodDays} Days` : null} />
                                        <ViewField label="Separation Date" value={formatDate(profile.separationDate)} />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-emerald-50/30 p-5 rounded-2xl border border-emerald-100">
                                        <div className="md:col-span-4 flex items-center gap-2 mb-2">
                                            <ShieldCheckIcon className="w-4 h-4 text-emerald-600" />
                                            <h4 className="text-[10px] font-black text-emerald-900 uppercase tracking-widest leading-none">Probationary Controls</h4>
                                        </div>
                                        <ViewField label="Current Probationary Status" value={profile.confirmationStatus} />
                                        <ViewField label="Probation Duration" value={profile.probationMonths ? `${profile.probationMonths} Months` : null} />
                                        <ViewField label="Probation End Date" value={formatDate(profile.probationEndDate)} />
                                        <ViewField label="Review Cycle Date" value={formatDate(profile.probationReviewDate)} />
                                    </div>
                                </div>
                            )}

                            {/* TAB: Financial Records */}
                            {activeTab === 'financial' && (
                                <div className="space-y-10">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                        <div className="md:col-span-3">
                                            <SectionHeader title="Payroll Config" icon={BanknotesIcon} color="amber" />
                                        </div>
                                        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-3xl p-8 text-white shadow-xl ring-4 ring-amber-100/50">
                                            <div className="flex justify-between items-start mb-6">
                                                <p className="text-[10px] font-black opacity-80 uppercase tracking-[0.2em] leading-none">Net Monthly Basic</p>
                                                <p className="text-xl font-black bg-white/20 px-3 py-1 rounded-full leading-none">{profile.currency}</p>
                                            </div>
                                            <h2 className="text-4xl font-black tracking-tighter mb-8">
                                                {profile.basicSalary?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </h2>
                                            <div className="flex justify-between items-end">
                                                <div className="space-y-1">
                                                    <p className="text-[9px] opacity-70 uppercase font-black">Pay Frequency</p>
                                                    <p className="text-sm font-bold bg-white/10 px-2 py-0.5 rounded inline-block">{profile.paymentFrequency}</p>
                                                </div>
                                                <CheckBadgeIcon className="w-8 h-8 opacity-40" />
                                            </div>
                                        </div>
                                        <div className="md:col-span-2 grid grid-cols-2 gap-4 h-fit">
                                            <ViewField label="Default Currency" value={profile.currency} />
                                            <ViewField label="Tax Identification (TIN)" value={profile.taxIdentificationNo} />
                                            <ViewField label="Official Payroll Basis" value={profile.paymentFrequency} />
                                            <ViewField label="Statutory Compliance" value="Fully Compliant" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="md:col-span-4">
                                            <SectionHeader title="Banking Disbursement Registry" icon={BriefcaseIcon} color="amber" />
                                        </div>
                                        <ViewField label="Financial Institution" value={profile.bankingDetails?.bankName} className="md:col-span-2" />
                                        <ViewField label="Branch Specification" value={profile.bankingDetails?.branchName} className="md:col-span-2" />
                                        <ViewField label="Registered Account No" value={profile.bankingDetails?.accountNo} className="md:col-span-2" />
                                        <ViewField label="Beneficiary Name" value={profile.bankingDetails?.accountHolderName || profile.fullName} className="md:col-span-2" />
                                        <ViewField label="Bank Numeric Code" value={profile.bankingDetails?.branchCode} />
                                        <ViewField label="SWIFT / BIC Universal" value={profile.bankingDetails?.swiftCode} />
                                        <ViewField label="IBAN Int. Registry" value={profile.bankingDetails?.iban} className="md:col-span-2" />
                                    </div>
                                </div>
                            )}

                            {/* TAB: Emergency & Others */}
                            {activeTab === 'others' && (
                                <div className="space-y-10">
                                    <div className="space-y-6">
                                        <SectionHeader title="Emergency Red-Alert Contacts" icon={ShieldCheckIcon} color="rose" />
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {profile.emergencyContacts?.map((con, i) => (
                                                <div key={i} className={`p-6 rounded-3xl border transition-all ${con.isPrimary ? 'border-rose-200 bg-rose-50/50 shadow-md ring-1 ring-rose-100' : 'border-gray-200 bg-gray-50'}`}>
                                                    <div className="flex justify-between items-start mb-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`p-2 rounded-xl bg-white shadow-sm ${con.isPrimary ? 'text-rose-500' : 'text-gray-400'}`}>
                                                                <UserIcon className="w-5 h-5" />
                                                            </div>
                                                            <div>
                                                                <p className="text-[10px] font-black text-gray-400 uppercase leading-none mb-1">Contact Name</p>
                                                                <h4 className="font-black text-gray-900 leading-none">{con.name}</h4>
                                                            </div>
                                                        </div>
                                                        {con.isPrimary && <span className="text-[8px] font-black bg-rose-600 text-white px-2 py-1 rounded-full tracking-tighter shadow-sm">PRIMARY</span>}
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-6 pl-10 border-l border-gray-200 ml-4 py-2">
                                                        <div>
                                                            <p className="text-[10px] font-black text-gray-400 uppercase leading-none mb-1">Relation</p>
                                                            <p className="text-xs font-black text-rose-600 uppercase tracking-tighter">{con.relationship}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] font-black text-gray-400 uppercase leading-none mb-1">Direct Dial</p>
                                                            <p className="text-sm font-black text-gray-900 tracking-tight">{con.contactNo}</p>
                                                        </div>
                                                    </div>
                                                    {con.address && (
                                                        <div className="mt-4 pt-4 border-t border-gray-200/50 flex gap-3">
                                                            <MapPinIcon className="w-3 h-3 text-gray-300 shrink-0 mt-0.5" />
                                                            <p className="text-[10px] text-gray-500 leading-relaxed italic">{con.address}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                                        <div className="space-y-4">
                                            <SectionHeader title="Academic Credentials" icon={AcademicCapIcon} color="slate" />
                                            {profile.education?.map((edu, i) => (
                                                <div key={i} className="p-4 bg-slate-50 rounded-2xl border border-slate-200 shadow-sm">
                                                    <p className="text-[10px] font-black text-indigo-600 uppercase mb-1">{edu.degree}</p>
                                                    <p className="text-xs font-black text-gray-900">{edu.institution}</p>
                                                    <p className="text-[10px] font-medium text-gray-400 mt-1 uppercase tracking-widest">Graduated: {edu.graduationYear}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="space-y-4">
                                            <SectionHeader title="Digital Dossier (Vault)" icon={DocumentTextIcon} color="slate" />
                                            <div className="grid grid-cols-1 gap-3">
                                                {profile.documents?.map((doc, idx) => (
                                                    <a key={idx} href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-2xl hover:border-indigo-500 hover:shadow-lg transition-all group overflow-hidden">
                                                        <div className="p-2.5 bg-gray-50 rounded-xl group-hover:bg-indigo-600 transition-colors">
                                                            <DocumentTextIcon className="w-5 h-5 text-gray-400 group-hover:text-white" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[10px] font-black text-gray-900 uppercase tracking-tight truncate leading-none">{doc.documentType || 'Dossier Entry'}</p>
                                                            <p className="text-[8px] text-gray-400 mt-1 font-black uppercase tracking-widest">Secure Storage Access</p>
                                                        </div>
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default ViewProfileSelf;
